import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  fetchErrorDiagnostics,
  fetchPlatformOpsSession,
} from "../api/errorDiagnostics";
import {
  formatDiagnosticsCopyText,
  sanitizeDiagnosticsForUi,
} from "./diagnosticsSanitize";

/**
 * Server-gated technical details for platform ops.
 * Ordinary safeMessage stays primary (parent). This block only appears after
 * a successful PlatformOps session probe + diagnostics fetch — never from
 * role-view simulation or client flags alone.
 *
 * Diagnostics live in component state only (no Redux / no shared cache).
 */
export function TechnicalErrorDetails({
  requestId,
  safeMessage,
  forceShowProbe = false,
}) {
  const { t } = useTranslation();
  const rid = requestId ? String(requestId).trim() : "";
  const [opsAllowed, setOpsAllowed] = useState(false);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [details, setDetails] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setDetails(null);
    setError(null);
    setOpen(false);
    setOpsAllowed(false);

    if (!rid && !forceShowProbe) return undefined;

    (async () => {
      const session = await fetchPlatformOpsSession();
      if (!cancelled) setOpsAllowed(Boolean(session.allowed));
    })();

    return () => {
      cancelled = true;
      setDetails(null);
    };
  }, [rid, forceShowProbe]);

  if (!rid || !opsAllowed) return null;

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchErrorDiagnostics(rid);
      if (!res.success || !res.data) {
        setDetails(null);
        setError(
          res.status === 403
            ? t("operation_feedback.diagnostics.forbidden")
            : t("operation_feedback.diagnostics.not_found")
        );
        return;
      }
      setDetails(sanitizeDiagnosticsForUi(res.data));
    } catch (_) {
      setDetails(null);
      setError(t("operation_feedback.diagnostics.load_failed"));
    } finally {
      setLoading(false);
    }
  };

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && !details && !loading) {
      await load();
    }
  };

  const copy = async () => {
    const text = formatDiagnosticsCopyText(details, {
      safeMessage,
      requestId: rid,
    });
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      }
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch (_) {
      /* ignore */
    }
  };

  return (
    <div className="mt-3 border-t border-slate-100 pt-3">
      <button
        type="button"
        className="text-sm font-medium text-slate-600 underline hover:text-slate-900"
        onClick={toggle}
        aria-expanded={open}
      >
        {t("operation_feedback.diagnostics.toggle")}
      </button>

      {open && (
        <div className="mt-2 rounded-lg bg-slate-50 p-3 text-xs text-slate-700">
          {loading && (
            <p role="status">{t("operation_feedback.diagnostics.loading")}</p>
          )}
          {error && !loading && (
            <p role="status" className="text-slate-600">
              {error}
            </p>
          )}
          {details && !loading && (
            <>
              <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
                {Object.entries(details).map(([key, value]) => (
                  <div key={key} className="contents">
                    <dt className="font-semibold text-slate-500">{key}</dt>
                    <dd className="font-mono break-all">{String(value)}</dd>
                  </div>
                ))}
              </dl>
              <button
                type="button"
                className="mt-3 inline-flex rounded border border-slate-200 bg-white px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-100"
                onClick={copy}
              >
                {copied
                  ? t("operation_feedback.diagnostics.copied")
                  : t("operation_feedback.diagnostics.copy")}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default TechnicalErrorDetails;
